/**
 * Sentry Error Tracking Configuration
 *
 * Provides centralized error tracking for the StartNCLEX application.
 *
 * Setup:
 * 1. Create a Sentry project at https://sentry.io
 * 2. Get your DSN from Project Settings > Client Keys
 * 3. Add REACT_APP_SENTRY_DSN to your .env file
 *
 * Features:
 * - Automatic error capturing
 * - Performance monitoring
 * - User context tracking
 * - Environment-specific configuration
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry
 * Call this in index.js before rendering the app
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  // Only initialize if DSN is configured
  if (!dsn) {
    console.log('Sentry DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_VERSION || '1.0.0',

    // Performance Monitoring - 10% in production for cost control
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Session Replay - 10% of normal sessions, 100% on errors
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extension errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Auth errors that are expected (user not logged in)
      'Login required',
    ],

    // Before sending error, you can modify or filter it
    beforeSend(event, hint) {
      // Filter or modify events if needed
      return event;
    },
  });
}

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export function setUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.sub || user.id,
    email: user.email,
    // Don't include sensitive data like names
  });
}

/**
 * Clear user context
 * Call this on logout
 */
export function clearUser() {
  Sentry.setUser(null);
}

/**
 * Capture an exception manually
 */
export function captureException(error, context = {}) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message (for non-error events)
 */
export function captureMessage(message, level = 'info', context = {}) {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message, category = 'action', data = {}) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Start a performance transaction
 */
export function startTransaction(name, op = 'navigation') {
  return Sentry.startInactiveSpan({
    name,
    op,
  });
}

/**
 * Test Sentry integration - call this to verify errors are being captured
 * Can be triggered from browser console: window.testSentry()
 */
export function testSentry() {
  try {
    throw new Error('Sentry Test Error - ' + new Date().toISOString());
  } catch (e) {
    Sentry.captureException(e);
    console.log('[Sentry] Test error sent! Check your Sentry dashboard.');
    return 'Test error sent to Sentry';
  }
}

// Expose test function globally for easy testing from console
if (typeof window !== 'undefined') {
  window.testSentry = testSentry;
}

/**
 * Sentry ErrorBoundary wrapper for easier use
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Higher-order component to wrap routes with Sentry profiling
 */
export const withSentryProfiler = Sentry.withProfiler;

export default Sentry;
